import { useEffect, useRef } from 'react';
import type { DebugSnapshot, DebugVariable, MemoryRegion } from '../lib/debug/types';
import { hexAddress } from '../lib/memory';

export function DebugInspector({
	snapshot,
	paused,
	onInspect,
	frameId,
	onSelectFrame,
	highlightedVariableKey,
	revealHighlight,
	onHoverVariable,
}: {
	snapshot: DebugSnapshot | null;
	paused: boolean;
	onInspect: (address: number, region?: MemoryRegion) => void;
	frameId: number | undefined;
	onSelectFrame: (id: number) => void;
	highlightedVariableKey: string | null;
	revealHighlight: boolean;
	onHoverVariable: (key: string | null) => void;
}) {
	const panel = useRef<HTMLElement>(null);
	useEffect(() => {
		if (highlightedVariableKey && revealHighlight)
			panel.current?.querySelector('.variable-linked')?.scrollIntoView({ block: 'nearest' });
	}, [highlightedVariableKey, revealHighlight]);
	const selected =
		snapshot?.frames.find((frame) => frame.id === frameId) ?? snapshot?.frames.at(-1);
	function followPointer(address: number) {
		const target = [
			...(snapshot?.globals ?? []),
			...(snapshot?.frames.flatMap((frame) => frame.variables) ?? []),
		]
			.filter(
				(variable) => address >= variable.address && address < variable.address + variable.byteSize,
			)
			.sort((a, b) => a.byteSize - b.byteSize)[0];
		onInspect(
			address,
			target
				? {
						address,
						byteSize: target.address + target.byteSize - address,
						label: `${target.name} (pointer target)`,
					}
				: undefined,
		);
	}
	const variables = (values: DebugVariable[]) =>
		values.length ? (
			<div className="variable-list">
				{values.map((variable) => {
					const pointer = variable.pointer;
					return (
						// biome-ignore lint/a11y/noStaticElementInteractions: hover/focus only highlights the variable elsewhere; no activation action
						<div
							key={variable.key}
							className={`debug-variable ${variable.changed ? 'variable-changed' : ''} ${highlightedVariableKey === variable.key ? 'variable-linked' : ''}`}
							data-testid={`variable-${variable.name}`}
							data-variable-key={variable.key}
							tabIndex={paused ? 0 : undefined}
							onMouseEnter={() => {
								if (paused) onHoverVariable(variable.key);
							}}
							onMouseLeave={() => onHoverVariable(null)}
							onFocus={() => {
								if (paused) onHoverVariable(variable.key);
							}}
							onBlur={(event) => {
								if (!event.currentTarget.contains(event.relatedTarget)) onHoverVariable(null);
							}}
						>
							<div className="variable-heading">
								<strong>{variable.name}</strong>
								<span>{variable.type}</span>
							</div>
							<code className="variable-value">{variable.value}</code>
							<div className="variable-storage">
								<button
									type="button"
									disabled={!paused}
									onClick={() =>
										onInspect(variable.address, {
											address: variable.address,
											byteSize: variable.byteSize,
											label: variable.name,
										})
									}
									title={`Inspect storage for ${variable.name}`}
								>
									{hexAddress(variable.address)}
								</button>
								<span>{variable.byteSize} bytes</span>
								{pointer !== undefined && pointer !== 0 && (
									<button
										type="button"
										disabled={!paused}
										onClick={() => followPointer(pointer)}
										title="Inspect raw bytes at the pointer; object validity is not guaranteed"
									>
										Follow pointer →
									</button>
								)}
							</div>
						</div>
					);
				})}
			</div>
		) : (
			<p className="debug-empty muted">No variables in this scope yet.</p>
		);

	return (
		<section className="debug-inspector" aria-label="Live debugger" ref={panel}>
			<div className="panel-heading">
				<h2>Debugger</h2>
				<span className="tag">{paused ? 'Paused' : 'Last checkpoint'}</span>
			</div>
			{!snapshot ? (
				<p className="debug-empty muted">Waiting for the first source checkpoint…</p>
			) : (
				<>
					<div className="debug-section-heading">
						Call stack <span>Checkpoint {snapshot.sequence}</span>
					</div>
					<div className="call-stack">
						{[...snapshot.frames].reverse().map((frame) => (
							<button
								type="button"
								key={frame.id}
								aria-pressed={selected?.id === frame.id}
								onClick={() => onSelectFrame(frame.id)}
							>
								<span>{frame.name}()</span>
								<span>main.c:{frame.line}</span>
							</button>
						))}
					</div>
					<div className="debug-section-heading">Locals · {selected?.name ?? '—'}</div>
					{variables(selected?.variables ?? [])}
					<div className="debug-section-heading">Globals</div>
					{variables(snapshot.globals)}
				</>
			)}
			<p className="debug-footnote">
				Values are read from instrumented C storage. Variables without a declaration initializer
				remain “initialization not tracked,” even after assignment. Aggregate previews are bounded;
				structs use raw bytes.
			</p>
		</section>
	);
}
